import { FC, useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TablePagination,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stack,
    Tooltip,
    Switch,
    FormControlLabel,
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
} from '@mui/icons-material';
import { useNotices, useCreateNotice, useUpdateNotice, useDeleteNotice } from '../../hooks/useNotice';
import type { NoticeItem, NoticePlatform, CreateNoticeRequest, UpdateNoticeRequest } from '../../types/notice';
import { PLATFORM_LABELS } from '../../types/notice';
import {
    formatKstDateTime,
    kstInputToUtcLocalDateTime,
    nowKstDateTimeInputValue,
    toKstDateTimeInputValue,
} from '../../utils/dateTime';

const PLATFORM_CHIP_COLORS: Record<NoticePlatform, 'default' | 'primary' | 'success'> = {
    ALL: 'primary',
    ANDROID: 'success',
    IOS: 'default',
};

const formatDate = (dateStr?: string) => {
    return formatKstDateTime(dateStr);
};

interface FormState {
    title: string;
    content: string;
    priority: number;
    platform: NoticePlatform;
    minAppVersionCode: string;
    maxAppVersionCode: string;
    isActive: boolean;
    startAt: string;
    endAt: string;
}

const defaultForm = (): FormState => ({
    title: '',
    content: '',
    priority: 0,
    platform: 'ALL',
    minAppVersionCode: '',
    maxAppVersionCode: '',
    isActive: true,
    startAt: nowKstDateTimeInputValue(),
    endAt: '',
});

const noticeToForm = (notice: NoticeItem): FormState => ({
    title: notice.title,
    content: notice.content,
    priority: notice.priority,
    platform: notice.platform,
    minAppVersionCode: notice.minAppVersionCode?.toString() ?? '',
    maxAppVersionCode: notice.maxAppVersionCode?.toString() ?? '',
    isActive: notice.isActive,
    startAt: toKstDateTimeInputValue(notice.startAt),
    endAt: toKstDateTimeInputValue(notice.endAt),
});

export const NoticeList: FC = () => {
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(20);
    const [activeFilter, setActiveFilter] = useState<'all' | 'true' | 'false'>('all');
    const [dialogMode, setDialogMode] = useState<'create' | 'edit' | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form, setForm] = useState<FormState>(defaultForm());

    const isActiveParam = activeFilter === 'all' ? undefined : activeFilter === 'true';

    const { data, isLoading } = useNotices({ page, size, isActive: isActiveParam });
    const createNotice = useCreateNotice();
    const updateNotice = useUpdateNotice();
    const deleteNotice = useDeleteNotice();

    const notices = data?.content ?? [];
    const totalCount = data?.totalCount ?? 0;

    const handleOpenCreate = () => {
        setForm(defaultForm());
        setEditingId(null);
        setDialogMode('create');
    };

    const handleOpenEdit = (notice: NoticeItem) => {
        setForm(noticeToForm(notice));
        setEditingId(notice.id);
        setDialogMode('edit');
    };

    const handleClose = () => {
        setDialogMode(null);
        setEditingId(null);
    };

    const handleDelete = (id: number, title: string) => {
        if (!window.confirm(`"${title}" 공지사항을 삭제하시겠습니까?`)) return;
        deleteNotice.mutate(id);
    };

    const handleSave = () => {
        const minVersionCode = form.minAppVersionCode ? parseInt(form.minAppVersionCode) : undefined;
        const maxVersionCode = form.maxAppVersionCode ? parseInt(form.maxAppVersionCode) : undefined;

        if (dialogMode === 'create') {
            const req: CreateNoticeRequest = {
                title: form.title,
                content: form.content,
                priority: form.priority,
                platform: form.platform,
                minAppVersionCode: minVersionCode,
                maxAppVersionCode: maxVersionCode,
                startAt: kstInputToUtcLocalDateTime(form.startAt) ?? '',
                endAt: kstInputToUtcLocalDateTime(form.endAt),
            };
            createNotice.mutate(req, { onSuccess: handleClose });
        } else if (dialogMode === 'edit' && editingId != null) {
            const req: UpdateNoticeRequest = {
                title: form.title,
                content: form.content,
                priority: form.priority,
                platform: form.platform,
                minAppVersionCode: minVersionCode,
                maxAppVersionCode: maxVersionCode,
                isActive: form.isActive,
                startAt: kstInputToUtcLocalDateTime(form.startAt) ?? '',
                endAt: kstInputToUtcLocalDateTime(form.endAt),
            };
            updateNotice.mutate({ id: editingId, data: req }, { onSuccess: handleClose });
        }
    };

    const isPending = createNotice.isPending || updateNotice.isPending;

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">공지사항 관리</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
                    공지사항 작성
                </Button>
            </Box>

            {/* Filters */}
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>상태</InputLabel>
                    <Select
                        value={activeFilter}
                        label="상태"
                        onChange={(e) => { setActiveFilter(e.target.value as typeof activeFilter); setPage(1); }}
                    >
                        <MenuItem value="all">전체</MenuItem>
                        <MenuItem value="true">활성</MenuItem>
                        <MenuItem value="false">비활성</MenuItem>
                    </Select>
                </FormControl>
            </Stack>

            {/* Table */}
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>제목</TableCell>
                            <TableCell>플랫폼</TableCell>
                            <TableCell>우선순위</TableCell>
                            <TableCell>상태</TableCell>
                            <TableCell>노출 시작</TableCell>
                            <TableCell>노출 종료</TableCell>
                            <TableCell>버전 범위</TableCell>
                            <TableCell align="center">작업</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                    로딩 중...
                                </TableCell>
                            </TableRow>
                        ) : notices.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                    공지사항이 없습니다.
                                </TableCell>
                            </TableRow>
                        ) : (
                            notices.map((notice) => (
                                <TableRow key={notice.id} hover>
                                    <TableCell>{notice.id}</TableCell>
                                    <TableCell>
                                        <Tooltip title={notice.content}>
                                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                {notice.title}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={PLATFORM_LABELS[notice.platform] ?? notice.platform}
                                            color={PLATFORM_CHIP_COLORS[notice.platform] ?? 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{notice.priority}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={notice.isActive ? '활성' : '비활성'}
                                            color={notice.isActive ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">{formatDate(notice.startAt)}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">{notice.endAt ? formatDate(notice.endAt) : '무제한'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" color="text.secondary">
                                            {notice.minAppVersionCode || notice.maxAppVersionCode
                                                ? `${notice.minAppVersionCode ?? '*'} ~ ${notice.maxAppVersionCode ?? '*'}`
                                                : '전체'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton size="small" onClick={() => handleOpenEdit(notice)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleDelete(notice.id, notice.title)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page - 1}
                    onPageChange={(_, newPage) => setPage(newPage + 1)}
                    rowsPerPage={size}
                    onRowsPerPageChange={(e) => { setSize(parseInt(e.target.value, 10)); setPage(1); }}
                    rowsPerPageOptions={[10, 20, 50]}
                    labelRowsPerPage="페이지당"
                />
            </TableContainer>

            {/* Create / Edit Dialog */}
            <Dialog open={dialogMode !== null} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{dialogMode === 'create' ? '공지사항 작성' : '공지사항 수정'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="제목"
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            fullWidth
                            required
                            inputProps={{ maxLength: 200 }}
                        />
                        <TextField
                            label="내용"
                            value={form.content}
                            onChange={(e) => setForm({ ...form, content: e.target.value })}
                            fullWidth
                            required
                            multiline
                            rows={5}
                        />
                        <Stack direction="row" spacing={2}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>플랫폼</InputLabel>
                                <Select
                                    value={form.platform}
                                    label="플랫폼"
                                    onChange={(e) => setForm({ ...form, platform: e.target.value as NoticePlatform })}
                                >
                                    {(Object.keys(PLATFORM_LABELS) as NoticePlatform[]).map((p) => (
                                        <MenuItem key={p} value={p}>{PLATFORM_LABELS[p]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                label="우선순위"
                                type="number"
                                value={form.priority}
                                onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 0 })}
                                sx={{ minWidth: 120 }}
                                helperText="높을수록 상단"
                            />
                        </Stack>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="노출 시작"
                                type="datetime-local"
                                value={form.startAt}
                                onChange={(e) => setForm({ ...form, startAt: e.target.value })}
                                fullWidth
                                required
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                label="노출 종료 (비워두면 무제한)"
                                type="datetime-local"
                                value={form.endAt}
                                onChange={(e) => setForm({ ...form, endAt: e.target.value })}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            />
                        </Stack>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="최소 앱 버전 코드"
                                type="number"
                                value={form.minAppVersionCode}
                                onChange={(e) => setForm({ ...form, minAppVersionCode: e.target.value })}
                                fullWidth
                                helperText="비워두면 제한 없음"
                            />
                            <TextField
                                label="최대 앱 버전 코드"
                                type="number"
                                value={form.maxAppVersionCode}
                                onChange={(e) => setForm({ ...form, maxAppVersionCode: e.target.value })}
                                fullWidth
                                helperText="비워두면 제한 없음"
                            />
                        </Stack>
                        {dialogMode === 'edit' && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={form.isActive}
                                        onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                                    />
                                }
                                label={form.isActive ? '활성' : '비활성'}
                            />
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>취소</Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={isPending || !form.title || !form.content || !form.startAt}
                    >
                        {dialogMode === 'create' ? '작성' : '저장'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
